import { AlertTriangle } from 'lucide-react';
import { StatusBadge } from '@/components/shared/StatusBadge';

const ALERT_TYPE_LABELS = {
  BIOLOGY: "Biologie",
  HDJ_SESSION_MISSING: "Séance de chimio non retrouvée",
  HDJ_PROBLEM: "Problème HDJ",
};

export function describeAlertSource(alert) {
  if (alert.type && ALERT_TYPE_LABELS[alert.type]) return ALERT_TYPE_LABELS[alert.type];
  if (alert.type) return alert.type;
  if (alert.checkinId || (alert.checkinIds || []).length > 0) return "Check-in patient";
  return "Alerte";
}

function formatDateTime(value) {
  if (!value) return "";
  return new Date(value).toLocaleString("fr-FR", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
}

/**
 * Affichage des alertes actives du patient
 * Utilisé par NursePatientDetail et DoctorPatientDetail
 */
export default function ActiveAlertsCard({ 
  alerts, 
  patient, 
  onResolveAlert,
  maxVisible = 5 
}) {
  const activeAlerts = alerts.filter(a => a.status !== "RESOLVED");

  return (
    <div className="dash-card h-full flex flex-col" data-testid="active-alerts-card">
      <h3 className="font-semibold text-slate-800 mb-4 flex items-center gap-2">
        <AlertTriangle className="w-5 h-5 text-amber-600" />
        Alertes actives ({activeAlerts.length})
      </h3>
      
      {activeAlerts.length === 0 ? (
        <div className="min-h-[160px] h-full flex flex-col items-center justify-center text-center gap-2" data-testid="active-alerts-empty">
          <div className="w-10 h-10 rounded-full bg-emerald-50 text-primary flex items-center justify-center">
            <AlertTriangle className="w-5 h-5" />
          </div>
          <p className="text-sm font-medium text-slate-600">Aucune alerte active pour ce patient</p>
        </div>
      ) : (
        <div className="space-y-2 max-h-72 overflow-y-auto">
          {activeAlerts.slice(0, maxVisible).map(alert => {
            const reasons = (alert.reasons || []).slice(0, 3);
            const occurrences = Number(alert.occurrences || 1);
            return (
              <div key={alert.id} className="p-3 bg-slate-50 rounded-lg flex items-start justify-between gap-3" data-testid={`active-alert-${alert.id}`}>
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <StatusBadge status={alert.level || alert.patientStatus || patient?.status} size="sm" />
                    <span className="text-xs font-medium text-slate-700" data-testid={`active-alert-source-${alert.id}`}>
                      {describeAlertSource(alert)}
                    </span>
                    {occurrences > 1 && (
                      <span className="text-[11px] px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-800" data-testid={`active-alert-occurrences-${alert.id}`}>
                        {occurrences} check-ins
                      </span>
                    )}
                  </div>
                  {alert.message && (
                    <p className="text-xs text-slate-600 mt-1" data-testid={`active-alert-message-${alert.id}`}>{alert.message}</p>
                  )}
                  {reasons.length > 0 && (
                    <ul className="text-xs text-slate-600 mt-1 space-y-0.5" data-testid={`active-alert-reasons-${alert.id}`}>
                      {reasons.map((r, i) => <li key={i}>• {r}</li>)}
                      {(alert.reasons || []).length > 3 && <li className="text-slate-400">… {(alert.reasons || []).length - 3} autre(s)</li>}
                    </ul>
                  )}
                  <p className="text-[11px] text-slate-400 mt-1" data-testid={`active-alert-time-${alert.id}`}>
                    Créée le {formatDateTime(alert.createdAt)}
                    {alert.lastCheckinAt && occurrences > 1 ? ` · dernier check-in ${formatDateTime(alert.lastCheckinAt)}` : ""}
                  </p>
                </div>
                <button
                  onClick={() => onResolveAlert(alert.id)}
                  className="shrink-0 px-3 py-1.5 bg-emerald-50 text-emerald-600 rounded-lg text-xs font-medium hover:bg-emerald-100"
                  data-testid={`resolve-alert-${alert.id}`}
                >
                  Résoudre
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
